import React, { createContext, useContext, useEffect, useState } from 'react'

type MapAsArray<Key, Item> = [Key, Item][]
export type TypeShareSelected = 'SHAREFEED' | 'SHAREDRIVE'

type MultiSelectionContextType<Key = unknown, Item = unknown> = {
  items: Map<Key, Item>
  set: (key: Key, item: Item) => void
  remove: (key: Key) => void
  toggle: (key: Key, item: Item) => void
  reset: () => void

  isSelectionActive: boolean
  turnOnSelection: () => void
  turnOffSelection: () => void
  toggleSelection: () => void
  limit: number
  hasAchiviedLimit: boolean
}

export default function createMultiSelectionContext<
  Key = unknown,
  Item = unknown
>({
  initialValue,
  limit = 0
}: { initialValue?: MapAsArray<Key, Item>; limit?: number } = {}) {
  const multiSelectionContext = createContext<
    MultiSelectionContextType<Key, Item>
  >({
    items: new Map(initialValue ?? []),
    set: () => {},
    remove: () => {},
    toggle: () => {},
    reset: () => {},
    isSelectionActive: false,
    turnOnSelection: () => {},
    turnOffSelection: () => {},
    toggleSelection: () => {},
    limit,
    hasAchiviedLimit: false
  })

  const useMultiSelectionContext = () => useContext(multiSelectionContext)

  function MultiSelectionProvider({ children }: { children: React.ReactNode }) {
    const [items, setItems] = useState(new Map<Key, Item>(initialValue))
    const [isSelectionActive, setIsSelectionActive] = useState(false)
    const [hasAchiviedLimit, setHasAchiviedLimit] = useState(false)
    useEffect(() => {
      setHasAchiviedLimit(items.size === limit)
    }, [items])

    return (
      <multiSelectionContext.Provider
        value={{
          items,
          set(key, item) {
            if (limit && hasAchiviedLimit) return
            setItems((prev) => new Map(prev.set(key, item)))
          },
          remove(key) {
            setItems((prev) => (prev.delete(key) ? new Map(prev) : prev))
          },
          toggle(key, item) {
            setItems((prev) =>
              prev.get(key)
                ? prev.delete(key)
                  ? new Map(prev)
                  : prev
                : limit && hasAchiviedLimit
                  ? prev
                  : new Map(prev.set(key, item))
            )
          },
          reset() {
            setItems(new Map())
          },
          isSelectionActive,
          turnOnSelection() {
            if (isSelectionActive) return
            setIsSelectionActive(true)
          },
          turnOffSelection() {
            if (!isSelectionActive) return
            setIsSelectionActive(false)
            setItems(new Map<Key, Item>(initialValue))
          },
          toggleSelection() {
            setIsSelectionActive((prev) => !prev)
          },
          limit,
          hasAchiviedLimit
        }}
      >
        {children}
      </multiSelectionContext.Provider>
    )
  }

  return { MultiSelectionProvider, useMultiSelectionContext }
}
